import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaCheck, FaTimes, FaPlus, FaFilter, FaAward, FaCalendarAlt, FaUserCheck, FaHandshake, FaUserPlus, FaFileInvoiceDollar, FaImage, FaCheckCircle, FaExclamationCircle, FaReceipt, FaUpload } from 'react-icons/fa';
import PointRulesReferenceModal from './Points/PointRulesReferenceModal';
import ReferralBusinessAdmin from './ReferralBusinessAdmin';

interface Activity {
  id: number;
  type: string;
  giver_id: number;
  giver_name?: string;
  giver_company?: string;
  giver_profile?: string;
  receiver_id?: number;
  receiver_name?: string;
  receiver_company?: string;
  receiver_profile?: string;
  ref_person_name?: string;
  number?: string;
  amount?: number;
  details?: string;
  image?: string;
  receipt_url?: string;
  giver_confirmation?: number;
  receiver_confirmation?: number;
  status: 'PENDING' | 'VERIFIED' | 'REJECTED' | 'DELETED';
  points_awarded: number;
  date: string;
  created_at?: string;
  source?: string;
  request_source?: string;
  location?: string;
}

interface Member {
  id: number;
  name: string;
  company_name?: string;
}

const ActivityVerification: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [sourceFilter, setSourceFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Preview Image Modal
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [showRulesModal, setShowRulesModal] = useState(false);

  // Verify Modal State with Editable Fields
  const [verifyModal, setVerifyModal] = useState<Activity | null>(null);
  const [pointsInput, setPointsInput] = useState<number>(10);
  const [editAmount, setEditAmount] = useState<string>('');
  const [editRefPerson, setEditRefPerson] = useState<string>('');
  const [editDetails, setEditDetails] = useState<string>('');
  const [pointMasterRules, setPointMasterRules] = useState<any[]>([]);

  // Reset Points Modal State
  const [showResetPointsModal, setShowResetPointsModal] = useState(false);
  const [resetTarget, setResetTarget] = useState<string>('ALL');
  const [resetValue, setResetValue] = useState<number>(0);

  const getNormalizedStatus = (status?: string | null, is_deleted?: number) => {
    if (is_deleted === 1 || status === 'DELETED') return 'DELETED';
    if (!status || status.trim() === '' || status.toUpperCase() === 'PENDING') return 'PENDING';
    if (status.toUpperCase() === 'VERIFIED') return 'VERIFIED';
    if (status.toUpperCase() === 'REJECTED') return 'REJECTED';
    return status.toUpperCase();
  };

  const matchesType = (actType: string, filter: string) => {
    if (filter === 'ALL') return true;
    const t = (actType || '').toUpperCase();
    if (filter === 'R2R') return t === 'R2R' || t === 'R2R_MEETING';
    if (filter === 'BUSINESS_ACK') return t === 'BUSINESS_ACK' || t === 'BUSINESS' || t === 'TYFCB';
    if (filter === 'REFERRAL') return t === 'REFERRAL' || t === 'REFERENCE';
    if (filter === 'VISITOR_INVITE') return t === 'VISITOR_INVITE' || t === 'VISITOR';
    if (filter === 'FIELD_VISIT') return t === 'FIELD_VISIT';
    if (filter === 'TESTIMONIAL') return t === 'TESTIMONIAL';
    if (filter === 'THANK_YOU') return t === 'THANK_YOU' || t === 'THANK_YOU_NOTE';
    if (filter === 'ATTENDANCE') return t.startsWith('ATTENDANCE');
    return t === filter.toUpperCase();
  };

  const handleRestoreActivity = async (id: number) => {
    if (!window.confirm('Restore this deleted activity back to pending verification?')) return;
    try {
      await axios.post(`https://rmbbackend.rmbjaipur.com/auth/activities/${id}/restore`);
      alert('Activity restored successfully!');
      if (verifyModal?.id === id) setVerifyModal(null);
      fetchActivities();
    } catch (err) {
      console.error('Error restoring activity:', err);
      alert('Failed to restore activity');
    }
  };

  const handlePermanentDeleteActivity = async (id: number) => {
    if (!window.confirm('CRITICAL WARNING: This will PERMANENTLY delete this activity from the database forever! Continue?')) return;
    try {
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/activities/${id}/permanent`);
      alert('Activity permanently deleted!');
      if (verifyModal?.id === id) setVerifyModal(null);
      fetchActivities();
    } catch (err) {
      console.error('Error permanently deleting activity:', err);
      alert('Failed to permanently delete activity');
    }
  };

  const handleResetPointsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const targetLabel = resetTarget === 'ALL' ? 'ALL members' : 'selected member';
    if (!window.confirm(`Are you sure you want to reset points to ${resetValue} for ${targetLabel}?`)) return;

    try {
      await axios.post('https://rmbbackend.rmbjaipur.com/auth/members/reset-points', {
        target: resetTarget,
        newPoints: resetValue
      });
      alert(`Points successfully reset for ${targetLabel}!`);
      setShowResetPointsModal(false);
      fetchActivities();
    } catch (err) {
      console.error('Error resetting points:', err);
      alert('Failed to reset points.');
    }
  };

  // New Activity Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [newActivity, setNewActivity] = useState({
    type: 'R2R',
    giver_id: '',
    receiver_id: '',
    ref_person_name: '',
    number: '',
    amount: '',
    details: '',
    image: '',
    receipt_url: '',
    star_rating: 5,
    giver_confirmation: 1,
    receiver_confirmation: 1,
    date: new Date().toISOString().split('T')[0]
  });
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, isReceipt = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    
    if (isReceipt) setIsUploadingReceipt(true);
    else setIsUploading(true);

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (isReceipt) {
        setNewActivity((prev: any) => ({ ...prev, receipt_url: res.data.imagePath }));
      } else {
        setNewActivity((prev: any) => ({ ...prev, image: res.data.imagePath }));
      }
    } catch (err) {
      console.error('File upload failed:', err);
      alert('Failed to upload file.');
    } finally {
      setIsUploading(false);
      setIsUploadingReceipt(false);
    }
  };

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/activities');
      setActivities(res.data?.activities || []);
    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo');
      const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
      const activeMembers = list.filter((u: any) => (
        u.delete !== 1 && 
        u.role !== 'super-admin' && 
        (u.status === 'active' || !u.status)
      ));
      activeMembers.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
      setMembers(activeMembers);
    } catch (err) {
      console.error('Error fetching members in ActivityVerification:', err);
    }
  };

  const fetchPointMaster = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/point');
      const rules = Array.isArray(res.data) 
        ? res.data 
        : (Array.isArray(res.data?.data) ? res.data.data : []);
      setPointMasterRules(rules);
    } catch (e) {
      console.error('Error fetching point master rules:', e);
      setPointMasterRules([]);
    }
  };

  useEffect(() => {
    fetchActivities();
    fetchMembers();
    fetchPointMaster();
  }, []);

  const getMasterPoints = (actType: string) => {
    if (!Array.isArray(pointMasterRules)) return 10;
    const typeMap: Record<string, string[]> = {
      'R2R': ['R2RHost', 'R2RGuest', 'R2R'],
      'REFERRAL': ['Reference', 'REFERRAL'],
      'BUSINESS_ACK': ['Reference', 'BUSINESS_ACK'],
      'VISITOR_INVITE': ['Visitor', 'VISITOR_INVITE'],
      'FIELD_VISIT': ['FieldVisit', 'FIELD_VISIT'],
      'ATTENDANCE': ['Attendance', 'ATTENDANCE'],
      'ATTENDANCE_LATE': ['Late', 'ATTENDANCE_LATE'],
      'ATTENDANCE_SUBSTITUTE': ['Substitute', 'ATTENDANCE_SUBSTITUTE']
    };
    const searchKeys = typeMap[actType] || [actType];
    const match = pointMasterRules.find((p: any) => 
      searchKeys.some(k => k.toLowerCase() === (p.type || p.code || p.activity_name || '').toLowerCase())
    );
    return match ? Number(match.data || match.point || 10) : 10;
  };

  const openVerifyModal = (act: Activity) => {
    setVerifyModal(act);
    setPointsInput(act.points_awarded || getMasterPoints(act.type));
    setEditAmount(act.amount ? String(act.amount) : '');
    setEditRefPerson(act.ref_person_name || '');
    setEditDetails(act.details || '');
  };

  const handleVerify = async (status: 'VERIFIED' | 'REJECTED') => {
    if (!verifyModal) return;
    try {
      await axios.put(`https://rmbbackend.rmbjaipur.com/auth/activities/${verifyModal.id}/verify`, {
        status,
        points: Number(pointsInput),
        amount: editAmount ? Number(editAmount) : 0,
        ref_person_name: editRefPerson,
        details: editDetails
      });
      alert(`Activity ${status.toLowerCase()} successfully with updated details!`);
      setVerifyModal(null);
      fetchActivities();
    } catch (err) {
      console.error('Error verifying activity:', err);
      alert('Failed to update activity status');
    }
  };

  const handleDeleteActivity = async (id: number) => {
    if (!window.confirm('Are you sure you want to permanently delete this activity log?')) return;
    try {
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/activities/${id}`);
      alert('Activity deleted successfully!');
      if (verifyModal?.id === id) setVerifyModal(null);
      fetchActivities();
    } catch (err) {
      console.error('Error deleting activity:', err);
      alert('Failed to delete activity');
    }
  };

  const handleCreateActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newActivity.giver_id) {
      alert('Please select Member / Visitor!');
      return;
    }
    const uploadedProof = newActivity.image || newActivity.receipt_url;
    if (!uploadedProof) {
      alert('Photo / Proof upload is COMPULSORY to log activity!');
      return;
    }

    try {
      await axios.post('https://rmbbackend.rmbjaipur.com/auth/activities', {
        ...newActivity,
        amount: newActivity.amount ? Number(newActivity.amount) : 0,
        image: uploadedProof
      });
      alert('Activity logged successfully!');
      setShowAddModal(false);
      setNewActivity({
        type: 'R2R',
        giver_id: '',
        receiver_id: '',
        ref_person_name: '',
        number: '',
        amount: '',
        details: '',
        image: '',
        receipt_url: '',
        star_rating: 5,
        giver_confirmation: 1,
        receiver_confirmation: 1,
        date: new Date().toISOString().split('T')[0]
      });
      fetchActivities();
    } catch (err) {
      console.error('Error creating activity:', err);
      alert('Failed to create activity');
    }
  };

  const getActivityBadge = (t: string) => {
    switch (t) {
      case 'R2R':
        return <span className="bg-purple-100 text-purple-800 border border-purple-300 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1 w-max"><FaUserCheck /> R2R Meeting</span>;
      case 'BUSINESS_ACK':
        return <span className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1 w-max"><FaFileInvoiceDollar /> Business Closed (TYFCB)</span>;
      case 'REFERRAL':
        return <span className="bg-blue-100 text-blue-800 border border-blue-300 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1 w-max"><FaHandshake /> Referral Passed</span>;
      case 'FIELD_VISIT':
        return <span className="bg-indigo-100 text-indigo-800 border border-indigo-300 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1 w-max">📍 Field Visit</span>;
      case 'ATTENDANCE':
        return <span className="bg-teal-100 text-teal-800 border border-teal-300 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1 w-max"><FaCalendarAlt /> Attendance Present</span>;
      case 'ATTENDANCE_LATE':
        return <span className="bg-orange-100 text-orange-800 border border-orange-300 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1 w-max">⏰ Attendance Late</span>;
      case 'ATTENDANCE_SUBSTITUTE':
        return <span className="bg-cyan-100 text-cyan-800 border border-cyan-300 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1 w-max">👥 Substitute Present</span>;
      default:
        return <span className="bg-amber-100 text-amber-800 border border-amber-300 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1 w-max"><FaUserPlus /> Visitor Invite</span>;
    }
  };

  const getGiverLabel = (type: string) => {
    if (type === 'R2R') return 'Visitor (Initiator)';
    if (type === 'BUSINESS_ACK') return 'Business Giver';
    if (type === 'REFERRAL') return 'Member Giving Referral';
    if (type === 'FIELD_VISIT') return 'Visiting Member / Initiator';
    if (['ATTENDANCE', 'ATTENDANCE_LATE', 'ATTENDANCE_SUBSTITUTE'].includes(type)) return 'Attending Member';
    if (type === 'TESTIMONIAL') return 'Testimonial Author (Business Giver)';
    if (type === 'THANK_YOU_NOTE') return 'Thank You Note Author (Business Receiver)';
    return 'Inviting Member';
  };

  const getReceiverLabel = (type: string) => {
    if (type === 'R2R') return 'Host (Recipient)';
    if (type === 'BUSINESS_ACK') return 'Business Receiver';
    if (type === 'FIELD_VISIT') return 'Host Member / Location Host';
    if (type === 'REFERRAL') return 'Referred Person (Name & Phone)';
    if (type === 'TESTIMONIAL') return 'Testimonial Recipient (Business Receiver)';
    if (type === 'THANK_YOU_NOTE') return 'Thank You Note Recipient (Business Giver)';
    return 'Visitor Details / Host';
  };

  const filteredActivities = activities.filter((act) => {
    // 1. Status Filter
    const normStatus = getNormalizedStatus(act.status, (act as any).is_deleted);
    if (statusFilter !== 'ALL') {
      if (normStatus !== statusFilter) return false;
    } else {
      if (normStatus === 'DELETED') return false;
    }

    // 2. Type Filter
    if (!matchesType(act.type, typeFilter)) {
      return false;
    }

    // 3. Source / Origin Filter
    if (sourceFilter !== 'ALL') {
      const src = (act.source || act.request_source || 'Web Portal').toLowerCase();
      if (sourceFilter === 'Mobile Application' && !src.includes('mobile') && !src.includes('flutter')) return false;
      if (sourceFilter === 'Web Portal' && (src.includes('mobile') || src.includes('flutter'))) return false;
    }

    // 4. Search Query Filter
    if (searchQuery && searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const match = (
        (act.giver_name || '').toLowerCase().includes(q) ||
        (act.giver_company || '').toLowerCase().includes(q) ||
        (act.receiver_name || '').toLowerCase().includes(q) ||
        (act.receiver_company || '').toLowerCase().includes(q) ||
        (act.ref_person_name || '').toLowerCase().includes(q) ||
        (act.number || '').toLowerCase().includes(q) ||
        (act.details || '').toLowerCase().includes(q) ||
        (act.type || '').toLowerCase().includes(q) ||
        (act.location || '').toLowerCase().includes(q) ||
        String(act.amount || '').includes(q)
      );
      if (!match) return false;
    }

    return true;
  });

  return (
    <div className="p-4 sm:p-6 bg-slate-50 dark:bg-[#0B132B] min-h-screen space-y-6 text-slate-900 dark:text-slate-100">
      
      {/* Header & Filter Bar */}
      <div className="bg-white dark:bg-[#1E2B45] p-4 sm:p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-[#2A3B5C] flex flex-col md:flex-row justify-between items-start md:items-center gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white flex items-center gap-2">
            <FaAward className="text-[#FDBC12]" /> Activity Verification & Points Audit
          </h1>
          <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-1">Review member activities, proof images/invoices, verify closed business, and approve points.</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full md:w-auto">
          <button
            onClick={() => setShowRulesModal(true)}
            className="flex-1 sm:flex-none justify-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg shadow flex items-center gap-1.5 transition cursor-pointer"
          >
            <FaAward className="text-[#FDBC12]" /> Points Rules
          </button>
          <button
            onClick={() => setShowResetPointsModal(true)}
            className="flex-1 sm:flex-none justify-center px-3 py-2 bg-red-600 hover:bg-red-700 text-white font-black text-xs rounded-lg shadow flex items-center gap-1.5 transition cursor-pointer"
          >
            ⚡ Reset Points
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex-1 sm:flex-none justify-center px-4 py-2 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-lg shadow flex items-center gap-1.5 transition cursor-pointer"
          >
            <FaPlus /> Log Activity
          </button>
        </div>
      </div>

      {/* Unified Filter Dashboard */}
      <div className="bg-white dark:bg-[#1E2B45] p-4 sm:p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-[#2A3B5C] space-y-4">
        {/* Row 1: Status Filter Buttons */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pb-3 border-b border-slate-200 dark:border-[#2A3B5C]">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
            <span className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1 flex-shrink-0">
              <FaFilter /> Status:
            </span>
            {[
              { key: 'ALL', label: 'All Statuses', count: activities.filter(a => getNormalizedStatus(a.status, (a as any).is_deleted) !== 'DELETED').length },
              { key: 'PENDING', label: '⏳ Pending Verification', count: activities.filter(a => getNormalizedStatus(a.status, (a as any).is_deleted) === 'PENDING').length },
              { key: 'VERIFIED', label: '✅ Verified', count: activities.filter(a => getNormalizedStatus(a.status, (a as any).is_deleted) === 'VERIFIED').length },
              { key: 'REJECTED', label: '❌ Rejected', count: activities.filter(a => getNormalizedStatus(a.status, (a as any).is_deleted) === 'REJECTED').length },
              { key: 'DELETED', label: '🗑️ Trash Archive', count: activities.filter(a => getNormalizedStatus(a.status, (a as any).is_deleted) === 'DELETED').length }
            ].map((st) => (
              <button
                key={st.key}
                onClick={() => setStatusFilter(st.key)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition whitespace-nowrap cursor-pointer flex-shrink-0 flex items-center gap-1.5 ${
                  statusFilter === st.key
                    ? 'bg-[#0A2451] dark:bg-amber-500 text-white dark:text-slate-950 font-black shadow-md ring-2 ring-[#0A2451]/30 dark:ring-amber-400/40'
                    : 'bg-white dark:bg-[#131E32] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-[#2A3B5C]'
                }`}
              >
                <span>{st.label}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${
                  statusFilter === st.key
                    ? 'bg-white/20 text-white dark:text-slate-950'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200'
                }`}>
                  {st.count}
                </span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Origin:</span>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="p-1.5 text-xs bg-white dark:bg-[#131E32] text-slate-800 dark:text-slate-100 border border-slate-300 dark:border-[#2A3B5C] rounded-lg outline-none font-bold"
            >
              <option value="ALL">All Origins</option>
              <option value="Mobile Application">📱 Mobile App</option>
              <option value="Web Portal">🌐 Web Portal</option>
            </select>
          </div>
        </div>

        {/* Row 2: Category Type Filter Pills */}
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
          <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1 flex-shrink-0">
            Category:
          </span>
          {[
            { key: 'ALL', label: 'All Activities' },
            { key: 'R2R', label: '🤝 R2R Meeting' },
            { key: 'BUSINESS_ACK', label: '💰 Business Closed' },
            { key: 'REFERRAL', label: '🔗 Referral Passed' },
            { key: 'VISITOR_INVITE', label: '👥 Visitor Invites' },
            { key: 'FIELD_VISIT', label: '📍 Field Visit' },
            { key: 'TESTIMONIAL', label: '⭐ Testimonial' },
            { key: 'THANK_YOU', label: '💌 Thank You Note' },
            { key: 'ATTENDANCE', label: '📅 Attendance' }
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTypeFilter(t.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition whitespace-nowrap cursor-pointer flex-shrink-0 ${
                typeFilter === t.key
                  ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Row 3: Real-Time Search Bar */}
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Search activities by member name, company, recipient, contact number, details, or amount..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 bg-slate-50 dark:bg-[#131E32] border border-slate-300 dark:border-[#2A3B5C] focus:border-[#0A2451] dark:focus:border-[#FDBC12] rounded-xl text-xs text-slate-900 dark:text-white outline-none font-bold shadow-xs transition"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-2 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 font-bold"
            >
              ✕ Clear
            </button>
          )}
        </div>
      </div>

      {/* REFERRAL BUSINESS & CLOSED DEALS VERIFICATION PANEL (AS SHOWN IN PROMPT IMAGE) */}
      {(typeFilter === 'ALL' || typeFilter === 'REFERRAL' || typeFilter === 'BUSINESS_ACK') && (
        <div className="rounded-2xl overflow-hidden border border-[#2A3B5C] shadow-2xl">
          <ReferralBusinessAdmin
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
          />
        </div>
      )}

      {/* General Activities Audit Table */}
      <div className="bg-white dark:bg-[#1E2B45] rounded-2xl shadow-sm border border-slate-200 dark:border-[#2A3B5C] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead className="bg-slate-100 dark:bg-[#131E32] text-slate-700 dark:text-slate-300 font-bold border-b border-slate-200 dark:border-[#2A3B5C] uppercase">
              <tr>
                <th className="p-3">Activity Type</th>
                <th className="p-3">Source / Origin</th>
                <th className="p-3">Proof / Invoice</th>
                <th className="p-3">Giver / Visitor / Initiator</th>
                <th className="p-3">Receiver / Host / Lead</th>
                <th className="p-3">Amount (₹) & Details</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-center">Points</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={9} className="p-6 text-center text-gray-500 font-medium">Loading activity verification logs...</td>
                </tr>
              ) : filteredActivities.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-6 text-center text-gray-500 font-medium">No activity records found matching filters.</td>
                </tr>
              ) : (
                filteredActivities.map((act) => (
                  <tr key={act.id} className="hover:bg-gray-50 transition">
                    <td className="p-3 font-bold">
                      {getActivityBadge(act.type)}
                      <div className="text-[10px] text-gray-400 font-normal mt-1">{new Date(act.date || act.created_at || Date.now()).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                    </td>

                    <td className="p-3">
                      {(act.source === 'Mobile Application' || act.request_source === 'Mobile Application' || act.source === 'Flutter App') ? (
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-purple-100 text-purple-900 border border-purple-300 flex items-center gap-1 w-fit shadow-xs">
                          📱 Mobile App
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-blue-100 text-blue-900 border border-blue-300 flex items-center gap-1 w-fit shadow-xs">
                          🌐 Web Portal
                        </span>
                      )}
                    </td>

                    <td className="p-3">
                      {act.image || act.receipt_url ? (
                        <div className="flex items-center gap-1.5 cursor-pointer group" onClick={() => setPreviewImage(act.image || act.receipt_url || null)}>
                          <img
                            src={(act.image || act.receipt_url || '').startsWith('http') ? (act.image || act.receipt_url) : `https://rmbbackend.rmbjaipur.com/${(act.image || act.receipt_url)?.replace(/\\/g, '/')}`}
                            alt="Proof"
                            className="w-10 h-10 object-cover rounded-lg border border-gray-300 group-hover:scale-105 transition"
                          />
                          <span className="text-[10px] font-bold text-blue-600 group-hover:underline">View</span>
                        </div>
                      ) : (
                        <span className="text-[11px] text-gray-400 italic">No proof attached</span>
                      )}
                    </td>

                    <td className="p-3">
                      <div className="font-bold text-gray-900">{act.giver_name || `Member #${act.giver_id}`}</div>
                      <div className="text-[10px] text-gray-500">{act.giver_company || 'RMB Member'}</div>
                    </td>

                    <td className="p-3">
                      {act.type === 'VISITOR_INVITE' ? (
                        <div className="bg-amber-50 p-2 rounded-lg border border-amber-200 text-xs shadow-xs">
                          <div className="font-extrabold text-amber-950 flex items-center gap-1">
                            👤 {act.ref_person_name || act.details || 'Guest Visitor'}
                          </div>
                          {act.number && (
                            <div className="text-[10px] font-extrabold text-amber-800 mt-0.5">
                              📞 Contact: {act.number}
                            </div>
                          )}
                          {act.location && (
                            <div className="text-[10px] text-amber-700 mt-0.5">
                              📍 Venue: {act.location}
                            </div>
                          )}
                        </div>
                      ) : act.receiver_name ? (
                        <div>
                          <div className="font-bold text-gray-900">{act.receiver_name}</div>
                          <div className="text-[10px] text-gray-500">{act.receiver_company || 'RMB Member'}</div>
                        </div>
                      ) : act.ref_person_name ? (
                        <div>
                          <div className="font-bold text-gray-900">{act.ref_person_name}</div>
                          <div className="text-[10px] text-gray-500">{act.number || 'External Referral'}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400 font-normal">N/A</span>
                      )}
                    </td>

                    <td className="p-3">
                      {act.amount && Number(act.amount) > 0 ? (
                        <div className="font-black text-emerald-700">₹{Number(act.amount).toLocaleString('en-IN')}</div>
                      ) : (
                        <div className="text-gray-400 font-medium text-[11px]">Non-Monetary</div>
                      )}
                      <div className="text-[11px] text-gray-600 max-w-xs truncate">{act.details || 'No additional notes'}</div>
                    </td>

                    <td className="p-3 font-bold">
                      {act.status === 'VERIFIED' ? (
                        <span className="text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 flex items-center gap-1 w-max"><FaCheckCircle /> Verified</span>
                      ) : act.status === 'REJECTED' ? (
                        <span className="text-rose-700 bg-rose-50 px-2 py-0.5 rounded border border-rose-200 flex items-center gap-1 w-max"><FaExclamationCircle /> Rejected</span>
                      ) : (
                        <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 flex items-center gap-1 w-max">⏳ Pending</span>
                      )}
                    </td>

                    <td className="p-3 text-center">
                      <span className="font-black text-[#0A2451] bg-amber-100 border border-amber-300 px-2.5 py-1 rounded-full text-xs">
                        +{act.points_awarded || getMasterPoints(act.type)} Pts
                      </span>
                    </td>

                    <td className="p-3 text-right">
                      {act.status === 'DELETED' || (act as any).is_deleted === 1 ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleRestoreActivity(act.id)}
                            className="px-2.5 py-1.5 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-300 font-bold text-xs rounded-lg transition cursor-pointer flex items-center gap-1"
                            title="Restore Activity Back to Active List"
                          >
                            ♻️ Restore
                          </button>
                          <button
                            onClick={() => handlePermanentDeleteActivity(act.id)}
                            className="px-2.5 py-1.5 bg-red-600 text-white hover:bg-red-700 font-bold text-xs rounded-lg shadow-sm transition cursor-pointer flex items-center gap-1"
                            title="Permanently Delete Activity"
                          >
                            🔥 Delete Permanently
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1.5">
                          {act.status === 'VERIFIED' ? (
                            <button
                              onClick={() => openVerifyModal(act)}
                              className="px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-300 font-bold text-xs rounded-lg transition cursor-pointer"
                            >
                              ✏️ Edit Details & Points ({act.points_awarded || getMasterPoints(act.type)} pts)
                            </button>
                          ) : (
                            <button
                              onClick={() => openVerifyModal(act)}
                              className="px-3 py-1.5 bg-[#FDBC12] text-[#0A2451] hover:bg-amber-400 font-bold text-xs rounded-lg shadow-sm transition cursor-pointer"
                            >
                              Review & Approve
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteActivity(act.id)}
                            className="px-2.5 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 font-bold text-xs rounded-lg transition cursor-pointer flex items-center gap-1"
                            title="Move Activity to Trash Archive"
                          >
                            <FaTimes /> Trash
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Verify & Points Modal */}
      {verifyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto border border-gray-100">
            <h3 className="text-lg font-black text-gray-900 border-b pb-3 mb-4 flex items-center gap-2">
              <FaAward className="text-[#FDBC12]" /> Activity Verification & Proof Review
            </h3>

            {/* Details & Edit Fields Card */}
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-4 text-xs space-y-3">
              <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                <span className="font-bold text-gray-500 uppercase">Current Status:</span>
                <span className={`px-2.5 py-1 rounded-full font-black text-[11px] uppercase ${
                  verifyModal.status === 'VERIFIED' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                  verifyModal.status === 'REJECTED' ? 'bg-red-100 text-red-800 border border-red-300' :
                  'bg-amber-100 text-amber-800 border border-amber-300'
                }`}>
                  {verifyModal.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-gray-800 pt-1">
                <div><b className="text-gray-500 block mb-0.5">Activity Type:</b> <span className="font-extrabold text-gray-900 bg-white px-2 py-1 rounded border border-slate-200 inline-block">{verifyModal.type}</span></div>
                <div>
                  <b className="text-gray-500 block mb-0.5">Points to Award *</b>
                  <input
                    type="number"
                    value={pointsInput}
                    onChange={(e) => setPointsInput(Number(e.target.value))}
                    className="w-full p-1.5 border border-amber-300 rounded font-black text-amber-900 bg-amber-50 outline-none"
                  />
                </div>
                <div><b className="text-gray-500 block mb-0.5">{getGiverLabel(verifyModal.type)}:</b> <span className="font-bold text-gray-900">{verifyModal.giver_name}</span></div>
                {verifyModal.receiver_name && (
                  <div><b className="text-gray-500 block mb-0.5">{getReceiverLabel(verifyModal.type)}:</b> <span className="font-bold text-gray-900">{verifyModal.receiver_name}</span></div>
                )}
                <div className="col-span-2">
                  <b className="text-gray-500 block mb-0.5">Transaction / Business Amount (₹)</b>
                  <input
                    type="number"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                    placeholder="Enter amount in ₹..."
                    className="w-full p-1.5 border border-slate-300 rounded font-bold text-emerald-800 bg-white outline-none"
                  />
                </div>
                <div className="col-span-2">
                  <b className="text-gray-500 block mb-0.5">Referred Contact / Person Name</b>
                  <input
                    type="text"
                    value={editRefPerson}
                    onChange={(e) => setEditRefPerson(e.target.value)}
                    placeholder="Referred person full name..."
                    className="w-full p-1.5 border border-slate-300 rounded font-semibold text-slate-800 bg-white outline-none"
                  />
                </div>
                <div className="col-span-2">
                  <b className="text-gray-500 block mb-0.5">Details / Description / Remarks</b>
                  <textarea
                    rows={2}
                    value={editDetails}
                    onChange={(e) => setEditDetails(e.target.value)}
                    placeholder="Enter verification notes or details..."
                    className="w-full p-2 border border-slate-300 rounded text-slate-800 bg-white outline-none font-medium"
                  />
                </div>
              </div>
            </div>

            {/* Uploaded Proof Image / Invoice Preview */}
            <div className="mb-5 space-y-2">
              <label className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                <FaImage className="text-blue-600" /> Uploaded Proof Image / Receipt Invoice:
              </label>
              {(verifyModal.image || verifyModal.receipt_url) ? (
                <div className="relative group bg-slate-900 rounded-xl overflow-hidden border border-slate-300 shadow-inner max-h-64 flex items-center justify-center">
                  <img
                    src={(verifyModal.image || verifyModal.receipt_url)?.startsWith('http') ? (verifyModal.image || verifyModal.receipt_url) : `https://rmbbackend.rmbjaipur.com/${(verifyModal.image || verifyModal.receipt_url)?.replace(/^\//, '')}`}
                    alt="Proof Image"
                    className="max-h-60 w-auto object-contain cursor-pointer transition hover:scale-105"
                    onClick={() => setPreviewImage((verifyModal.image || verifyModal.receipt_url)?.startsWith('http') ? (verifyModal.image || verifyModal.receipt_url)! : `https://rmbbackend.rmbjaipur.com/${(verifyModal.image || verifyModal.receipt_url)?.replace(/^\//, '')}`)}
                  />
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-bold px-2.5 py-1 rounded-md flex items-center gap-1 pointer-events-none">
                    <FaImage /> Click image to expand
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 rounded-xl border border-dashed border-gray-300 text-center text-xs text-gray-500 font-medium">
                  <FaExclamationCircle className="mx-auto text-amber-500 mb-1" size={18} />
                  No proof photo or receipt invoice was attached with this activity log.
                </div>
              )}
            </div>

            {/* Action Buttons: Accept / Reject / Delete / Cancel */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setVerifyModal(null)}
                  className="px-3.5 py-2 border border-gray-300 text-gray-700 font-bold text-xs rounded-xl hover:bg-gray-100 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteActivity(verifyModal.id)}
                  className="px-3.5 py-2 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 font-bold text-xs rounded-xl transition flex items-center gap-1 cursor-pointer"
                >
                  <FaTimes /> Delete Activity
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleVerify('REJECTED')}
                  className="px-3.5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-xl shadow transition flex items-center gap-1 cursor-pointer"
                >
                  <FaTimes /> Reject
                </button>
                <button
                  onClick={() => handleVerify('VERIFIED')}
                  className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs rounded-xl shadow transition flex items-center gap-1 cursor-pointer"
                >
                  <FaCheck /> Accept & Verify
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Activity Punch Modal with Invoice Upload */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 border-b pb-3 mb-4">
              ➕ Log New Member Activity / Referral
            </h3>

            <form onSubmit={handleCreateActivity} className="space-y-4 text-xs">
              <div>
                <label className="font-semibold text-gray-700 block mb-1">Activity Type *</label>
                <select
                  value={newActivity.type}
                  onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold"
                >
                  <option value="R2R">R2R Meeting (Visitor & Host)</option>
                  <option value="REFERRAL">Referral Business Passed</option>
                  <option value="BUSINESS_ACK">Closed Business (Business Giver & Receiver)</option>
                  <option value="VISITOR_INVITE">Visitor / Guest Invite</option>
                  <option value="TESTIMONIAL">Testimonial (Business Giver to Receiver)</option>
                  <option value="THANK_YOU_NOTE">Thank You Note (Business Receiver to Giver)</option>
                </select>
              </div>

              {/* Member Selection Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-gray-700 block mb-1">{getGiverLabel(newActivity.type)} *</label>
                  <select
                    value={newActivity.giver_id}
                    onChange={(e) => setNewActivity({ ...newActivity, giver_id: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold text-xs"
                    required
                  >
                    <option value="">Select Member</option>
                    {members.map(m => (
                      <option key={m.id} value={m.id}>{m.name} ({m.company_name || 'RMB'})</option>
                    ))}
                  </select>
                </div>

                {['R2R', 'BUSINESS_ACK', 'TESTIMONIAL', 'THANK_YOU_NOTE'].includes(newActivity.type) && (
                  <div>
                    <label className="font-bold text-gray-700 block mb-1">{getReceiverLabel(newActivity.type)} *</label>
                    <select
                      value={newActivity.receiver_id}
                      onChange={(e) => setNewActivity({ ...newActivity, receiver_id: e.target.value })}
                      className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold text-xs"
                      required
                    >
                      <option value="">Select Member</option>
                      {members.map(m => (
                        <option key={m.id} value={m.id}>{m.name} ({m.company_name || 'RMB'})</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Referral Details */}
              {['REFERRAL', 'VISITOR_INVITE'].includes(newActivity.type) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-bold text-gray-700 block mb-1">Referred Person Name</label>
                    <input
                      type="text"
                      value={newActivity.ref_person_name}
                      onChange={(e) => setNewActivity({ ...newActivity, ref_person_name: e.target.value })}
                      placeholder="Full Name"
                      className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-bold text-gray-700 block mb-1">Referred Contact Number</label>
                    <input
                      type="text"
                      value={newActivity.number}
                      onChange={(e) => setNewActivity({ ...newActivity, number: e.target.value })}
                      placeholder="Phone number"
                      className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
                    />
                  </div>
                </div>
              )}

              {/* Star Rating Input for Testimonial */}
              {newActivity.type === 'TESTIMONIAL' && (
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <label className="font-bold text-amber-900 block mb-1">Member Star Rating (1 - 5 Stars) *</label>
                  <select
                    value={newActivity.star_rating}
                    onChange={(e) => setNewActivity({ ...newActivity, star_rating: Number(e.target.value) })}
                    className="w-full p-2.5 border border-amber-300 rounded-lg outline-none font-extrabold text-xs text-amber-900 bg-white"
                  >
                    <option value="5">⭐⭐⭐⭐⭐ (5 Stars - Outstanding Experience)</option>
                    <option value="4">⭐⭐⭐⭐ (4 Stars - Highly Recommended)</option>
                    <option value="3">⭐⭐⭐ (3 Stars - Good Business Deal)</option>
                    <option value="2">⭐⭐ (2 Stars - Satisfactory)</option>
                    <option value="1">⭐ (1 Star - Needs Improvement)</option>
                  </select>
                </div>
              )}

              {/* Amount for Business / Referral */}
              {['BUSINESS_ACK', 'REFERRAL'].includes(newActivity.type) && (
                <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                  <label className="font-bold text-emerald-900 block mb-1">Business Closed Amount (₹)</label>
                  <input
                    type="number"
                    value={newActivity.amount}
                    onChange={(e) => setNewActivity({ ...newActivity, amount: e.target.value })}
                    placeholder="e.g. 50000"
                    className="w-full p-2.5 border border-emerald-300 rounded-lg outline-none font-bold text-xs"
                  />
                </div>
              )}

              {/* Compulsory Photo Upload Field for ALL Activity Types */}
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-300 space-y-2">
                <label className="font-bold text-gray-800 block text-xs flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    📸 Activity Proof / Photo Upload * <span className="text-red-600 font-extrabold">(Compulsory)</span>
                  </span>
                  {isUploading && <span className="text-amber-600 font-bold animate-pulse">Uploading...</span>}
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileUpload(e, false)}
                  className="w-full text-xs text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-[#02256D] file:text-[#FDBC12] cursor-pointer border border-gray-300 rounded-lg p-1.5 bg-white font-medium"
                />
                {newActivity.image ? (
                  <div className="text-xs font-bold text-emerald-700 flex items-center gap-1.5 pt-1">
                    <span>✅ Photo uploaded:</span>
                    <span className="truncate max-w-xs underline">{newActivity.image}</span>
                  </div>
                ) : (
                  <p className="text-[11px] text-amber-700 font-semibold">⚠️ Photo or receipt upload is required to submit this activity.</p>
                )}
              </div>

              {/* Details & Submit */}
              <div>
                <label className="font-bold text-gray-700 block mb-1">Description / Notes</label>
                <textarea
                  rows={2}
                  value={newActivity.details}
                  onChange={(e) => setNewActivity({ ...newActivity, details: e.target.value })}
                  placeholder="Enter activity description..."
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 border-t pt-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#FDBC12] text-[#0A2451] font-black rounded-lg shadow"
                >
                  Submit Activity
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-2xl w-full max-h-[85vh] bg-black p-2 rounded-xl border border-gray-700 flex flex-col items-center">
            <button className="absolute top-2 right-2 text-white font-black text-lg bg-red-600 px-3 py-1 rounded-full">✕</button>
            <img
              src={previewImage.startsWith('http') ? previewImage : `https://rmbbackend.rmbjaipur.com/${previewImage}`}
              alt="Full Preview"
              className="max-h-[80vh] w-auto object-contain rounded"
            />
          </div>
        </div>
      )}

      {/* Points Rules Reference Modal */}
      <PointRulesReferenceModal isOpen={showRulesModal} onClose={() => setShowRulesModal(false)} />

      {/* Reset Member Points Modal */}
      {showResetPointsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1E2B45] rounded-2xl shadow-2xl w-full max-w-md p-6 border border-slate-200 dark:border-[#2A3B5C] space-y-4 animate-scale-in text-slate-900 dark:text-slate-100">
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-[#2A3B5C] pb-3">
              <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
                ⚡ Reset Member Leaderboard Points
              </h3>
              <button
                type="button"
                onClick={() => setShowResetPointsModal(false)}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-white font-black text-lg p-1 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleResetPointsSubmit} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1.5">Select Reset Scope / Target *</label>
                <select
                  value={resetTarget}
                  onChange={(e) => setResetTarget(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 dark:border-[#2A3B5C] rounded-xl font-bold text-slate-800 dark:text-white outline-none bg-white dark:bg-[#131E32] focus:ring-2 focus:ring-red-500"
                >
                  <option value="ALL">🔥 ALL MEMBERS (Bulk Reset Leaderboard)</option>
                  {members.map(m => (
                    <option key={m.id} value={m.id}>👤 {m.name} ({m.company_name || 'RMB'})</option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
                  {resetTarget === 'ALL'
                    ? 'This will reset the total leaderboard points for ALL members across the chapter.'
                    : 'This will reset the total leaderboard points for only the selected member.'}
                </p>
              </div>

              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1.5">New Points Value</label>
                <input
                  type="number"
                  value={resetValue}
                  onChange={(e) => setResetValue(Number(e.target.value))}
                  placeholder="0"
                  className="w-full p-2.5 border border-slate-300 dark:border-[#2A3B5C] bg-white dark:bg-[#131E32] rounded-xl font-black text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-red-500"
                />
                <p className="text-[10px] text-slate-400 dark:text-slate-400 mt-1">
                  Default is 0 points (Full Reset).
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 dark:border-[#2A3B5C]">
                <button
                  type="button"
                  onClick={() => setShowResetPointsModal(false)}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-bold rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-black rounded-xl shadow cursor-pointer transition"
                >
                  Confirm & Reset Points
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ActivityVerification;
